import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useTeamStore } from '@/stores/teamStore';
import { useUIStore, useToast } from '@/stores/uiStore';
import { Button, Modal } from '@/components/common';
import { TeamSettings } from './TeamSettings';
import { AccountSettings } from './AccountSettings';
import { ArchitectureTab } from './ArchitectureTab';
import { SheetsSettings } from './SheetsSettings';
import * as authApi from '@/api/auth';

type SettingsTab = 'team' | 'account' | 'sheets' | 'architecture';
const TC_SHEETS_ALLOWED_EMAIL = 'andrew.winter@trueclassic.com';

export function SettingsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab: routeTab } = useParams<{ tab?: string }>();
  const user = useAuthStore((state) => state.user);
  const fetchTeams = useAuthStore((state) => state.fetchTeams);
  const currentTeam = useTeamStore((state) => state.currentTeam);
  const closeSettings = useUIStore((state) => state.closeSettings);
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<SettingsTab>(
    (routeTab as SettingsTab) || 'team'
  );
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const canUseSheets = user?.email?.toLowerCase() === TC_SHEETS_ALLOWED_EMAIL;

  useEffect(() => {
    if (routeTab && (routeTab === 'team' || routeTab === 'account' || routeTab === 'architecture' || (routeTab === 'sheets' && canUseSheets))) {
      setActiveTab(routeTab);
    } else if (routeTab === 'sheets' && !canUseSheets) {
      setActiveTab('account');
    }
  }, [routeTab, canUseSheets]);

  const handleBack = () => {
    closeSettings();
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    navigate(`/settings/${tab}`);
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;

    setIsCreating(true);
    try {
      await authApi.createTeam(newTeamName.trim());
      await fetchTeams();
      setShowCreateTeamModal(false);
      setNewTeamName('');
      toast.success('Team created successfully!');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to create team');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div id="settingsScreen" className="settings-container" style={{ display: 'block' }}>
      <div className="settings-header">
        <h1 className="settings-title">Settings</h1>
        <Button variant="outline" onClick={handleBack}>
          ← Back
        </Button>
      </div>

      <div className="settings-content">
        <div className="settings-tabs">
          <button
            className={`tab-button ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => handleTabChange('account')}
          >
            Account
          </button>
          <button
            className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => handleTabChange('team')}
          >
            Teams
          </button>
          {canUseSheets && (
            <button
              className={`tab-button ${activeTab === 'sheets' ? 'active' : ''}`}
              onClick={() => handleTabChange('sheets')}
            >
              Sheets
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'architecture' ? 'active' : ''}`}
            onClick={() => handleTabChange('architecture')}
          >
            Architecture
          </button>
        </div>

        <div className="settings-panel">
          {activeTab === 'team' && currentTeam && <TeamSettings team={currentTeam} />}

          {activeTab === 'team' && !currentTeam && (
            <div className="settings-empty-state">
              <h3>No Team Selected</h3>
              <p>Create a team or select one to manage its settings.</p>
              <Button variant="primary" onClick={() => setShowCreateTeamModal(true)}>
                Create Team
              </Button>
            </div>
          )}

          {activeTab === 'account' && user && (
            <AccountSettings user={user} />
          )}

          {activeTab === 'sheets' && canUseSheets && (
            <SheetsSettings team={currentTeam} />
          )}

          {activeTab === 'architecture' && <ArchitectureTab />}
        </div>
      </div>

      <Modal
        isOpen={showCreateTeamModal}
        onClose={() => setShowCreateTeamModal(false)}
        title="Create New Team"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowCreateTeamModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateTeam}
              isLoading={isCreating}
              disabled={!newTeamName.trim()}
            >
              Create Team
            </Button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Team Name</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Marketing Team"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
          />
        </div>
      </Modal>
    </div>
  );
}

export default SettingsScreen;
